import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, ShieldCheck, CalendarDays, User, ClipboardList } from "lucide-react";
import { fmtWhen } from "@/components/YearEndChecklist";

export const MONTHS_NB = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

export const defaultOppdragYear = () => new Date().getFullYear();
export const defaultOppdragMonth = () => new Date().getMonth() + 1;
export const oppdragYearOptions = () => {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2];
};

export const periodLabel = (frequency, year, month) =>
  frequency === "manedlig" ? `${MONTHS_NB[month - 1]} ${year}` : `Regnskapsår ${year}`;

const FREQ_BADGE = {
  manedlig: { label: "Månedlig", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  arlig: { label: "Årlig", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
};

function FreqBadge({ frequency }) {
  const b = FREQ_BADGE[frequency] || FREQ_BADGE.arlig;
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${b.cls}`}>{b.label}</span>;
}

function PointRow({ point, currentUserId, onToggle }) {
  const done = !!point.done;
  const qa = !!point.qa;
  const ownDone = point.done_by_id && point.done_by_id === currentUserId;
  return (
    <div className={`rounded-lg border p-3 ${qa ? "bg-emerald-500/5 border-emerald-500/25" : done ? "bg-secondary/60 border-border" : "bg-secondary/30 border-border"}`} data-testid={`oppdrag-point-${point.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{point.title}</p>
          {point.description && <p className="text-xs text-muted-foreground mt-0.5">{point.description}</p>}
          <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
            {point.due_hint && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {point.due_hint}</span>}
            {point.responsible_hint && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {point.responsible_hint}</span>}
          </div>
        </div>
        <div className="flex gap-5 shrink-0">
          <div className="text-center min-w-[92px]">
            <p className="label-caps mb-1">Utført</p>
            <Checkbox checked={done} onCheckedChange={() => onToggle(point.id, "done", !done)} data-testid={`oppdrag-done-${point.id}`} className="mx-auto" />
            {done && point.done_by && (
              <p className="text-[10px] text-muted-foreground leading-tight mt-1 flex flex-col items-center">
                <span className="inline-flex items-center gap-0.5 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> {point.done_by}</span>
                {fmtWhen(point.done_at)}
              </p>
            )}
          </div>
          <div className="text-center min-w-[110px]">
            <p className="label-caps mb-1">Kvalitetssikret</p>
            <Checkbox
              checked={qa}
              disabled={!done || (ownDone && !qa)}
              onCheckedChange={() => onToggle(point.id, "qa", !qa)}
              data-testid={`oppdrag-qa-${point.id}`}
              className="mx-auto"
            />
            {qa && point.qa_by && (
              <p className="text-[10px] text-muted-foreground leading-tight mt-1 flex flex-col items-center">
                <span className="inline-flex items-center gap-0.5 text-emerald-400"><ShieldCheck className="w-3 h-3" /> {point.qa_by}</span>
                {fmtWhen(point.qa_at)}
              </p>
            )}
            {done && !qa && ownDone && (
              <p className="text-[10px] text-amber-400/90 leading-tight mt-1">Må KS av en annen</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RoutineBlock({ cid, routine, currentUserId, year, month, onChange }) {
  const [det, setDet] = useState(routine);
  useEffect(() => { setDet(routine); }, [routine]);

  const toggle = async (pointId, field, value) => {
    try {
      const { data } = await api.put(`/customers/${cid}/oppdragsutforelse`, {
        routine_id: det.routine_id, point_id: pointId, field, value, year, month,
      });
      setDet((prev) => ({ ...prev, ...data }));
      onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`oppdrag-routine-${det.routine_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-display text-base">{det.routine_title}</h4>
          <FreqBadge frequency={det.frequency} />
          <span className="text-xs text-muted-foreground">{periodLabel(det.frequency, year, month)}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className={det.done_complete ? "text-emerald-400" : "text-muted-foreground"}>Utført {det.done_count}/{det.total}</span>
          <span className={det.qa_complete ? "text-emerald-400" : "text-muted-foreground"}>KS {det.qa_count}/{det.total}</span>
        </div>
      </div>
      <div className="space-y-2">
        {det.points.map((p) => (
          <PointRow key={p.id} point={p} currentUserId={currentUserId} onToggle={toggle} />
        ))}
      </div>
    </div>
  );
}

/**
 * Oppdragsutførelse for én kunde. Brukes både på kundekortet (egen periodevelger)
 * og inne i den sentrale oversikten (hideSelector + year/month fra parent).
 */
export function OppdragCustomerSection({ customerId, year: yearProp, month: monthProp, hideSelector, onChange, showEmpty }) {
  const { user } = useAuth();
  const [year, setYear] = useState(yearProp || defaultOppdragYear());
  const [month, setMonth] = useState(monthProp || defaultOppdragMonth());
  const [data, setData] = useState(null);

  useEffect(() => { if (yearProp) setYear(yearProp); }, [yearProp]);
  useEffect(() => { if (monthProp) setMonth(monthProp); }, [monthProp]);

  const load = useCallback(() => {
    let cancelled = false;
    api.get(`/customers/${customerId}/oppdragsutforelse?year=${year}&month=${month}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(apiError(e)); });
    return () => { cancelled = true; };
  }, [customerId, year, month]);
  useEffect(() => load(), [load]);

  const handleChange = () => { onChange && onChange(); };

  if (!data) return <p className="text-sm text-muted-foreground py-4">Laster…</p>;

  const body = data.routines.length === 0 ? (
    showEmpty ? (
      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="oppdrag-empty">
        <ClipboardList className="w-9 h-9 text-muted-foreground mb-3" strokeWidth={1.5} />
        <p className="font-display">Ingen oppdragsrutiner for denne kunden</p>
        <p className="text-muted-foreground text-sm mt-1 max-w-sm">Oppdragsrutiner vises når kunden har relevant produkt (lønn, fakturering, årsregnskap) eller ved foretaksomfattende rutiner.</p>
      </div>
    ) : null
  ) : (
    <div className="space-y-4">
      {data.routines.map((r) => (
        <RoutineBlock key={r.routine_id} cid={customerId} routine={r} currentUserId={user?.id} year={year} month={month} onChange={handleChange} />
      ))}
    </div>
  );

  if (hideSelector) return body;
  if (data.routines.length === 0 && !showEmpty) return null;

  return (
    <div data-testid="customer-oppdrag-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Oppdragsutførelse</h3>
          <p className="text-xs text-muted-foreground mt-0.5">To-trinns kontroll: utført og kvalitetssikret (av en annen person)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36 bg-secondary border-border" data-testid="oppdrag-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border max-h-72">
              {MONTHS_NB.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 bg-secondary border-border" data-testid="oppdrag-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {oppdragYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {body}
    </div>
  );
}
